import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, UserRound, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserSearch } from './events.api';

export interface PickedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Search a customer and pick them as a slot's host.
 *
 * The original repeated this block four times — in the basic create tab, the
 * advanced one, the edit modal and the add-booking form — each with its own
 * `searchText` state and its own antd Popconfirm asking "select this user?".
 * The confirm step is gone: picking a row from a list you just searched is not
 * a destructive action, and the choice is visibly reversible.
 */
export function HostPicker({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: PickedUser | null;
  onSelect: (user: PickedUser | null) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');

  const search = useUserSearch(term, term.trim().length > 0);
  const results = search.data?.results ?? [];

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>

      {selected ? (
        <div className="flex items-center gap-2 rounded-lg border p-2">
          <UserRound className="text-muted-foreground size-4 shrink-0" />
          <div className="min-w-0 flex-1 text-sm">
            <p className="truncate font-medium">
              {[selected.firstName, selected.lastName].filter(Boolean).join(' ')}
            </p>
            <p className="text-muted-foreground truncate text-xs">
              {selected.email}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('events.clearHost')}
            onClick={() => onSelect(null)}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('events.searchCustomer')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setTerm(draft);
                }
              }}
            />
            <Button
              variant="outline"
              onClick={() => setTerm(draft)}
              disabled={!draft.trim() || search.isFetching}
            >
              {search.isFetching ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
            </Button>
          </div>

          {term && search.isSuccess && results.length === 0 && (
            <p className="text-muted-foreground text-xs">
              {t('events.noCustomers')}
            </p>
          )}

          {results.length > 0 && (
            <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
              {results.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => {
                    onSelect(user);
                    setDraft('');
                    setTerm('');
                  }}
                  className="hover:bg-accent w-full p-2 text-left text-sm transition-colors"
                >
                  <p className="font-medium">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {user.email}
                  </p>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
